import type { ArtifactKind } from '@inference.engine/widgets';
import { Button } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { ScopedPermission } from '@/shared/ui';
import { FilterChipPopover, SearchInput, SortChipPopover } from '@/shared/ui';
import { ARTIFACT_KIND_FILTER_CHOICES, ARTIFACT_SORT_CHOICES } from '../../library.constants';
import { PendingSkeleton } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

/** Sentinel passed to BE as `folderId=""` to scope to root-only artifacts. */
// eslint-disable-next-line i18next/no-literal-string -- filter sentinel sent to the backend
export const FOLDER_FILTER_ROOT_VALUE = '__root__';

interface FolderOption {
  id: string;
  name: string;
}

interface Props {
  /** Swaps the controls for placeholders while the section is still resolving. */
  pending?: boolean;
  kindFilter: ArtifactKind | null;
  onKindFilterChange: (next: ArtifactKind | null) => void;
  folderFilter: string | null;
  onFolderFilterChange: (next: string | null) => void;
  folders: FolderOption[];
  search: string;
  onSearchChange: (next: string) => void;
  sort: string | null;
  onSortChange: (next: string | null) => void;
  onAddArtifactsClick: () => void;
}

export function ArtifactsToolbar(props: Props) {
  const { t } = useTranslation('projects');

  return (
    <div className="flex items-center justify-between gap-3 p-section">
      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <div className="flex items-center gap-2">
          <SearchInput
            value={props.search}
            onChange={props.onSearchChange}
            aria-label={t('projectDetail.artifactsToolbar.searchArtifacts')}
            className="w-80"
          />

          <SortChipPopover
            options={ARTIFACT_SORT_CHOICES}
            value={props.sort}
            onChange={props.onSortChange}
            resetOptionLabel={t('projectDetail.artifactsToolbar.newestFirst')}
          />

          <FilterChipPopover<ArtifactKind>
            label={t('projectDetail.artifactsToolbar.type')}
            value={props.kindFilter}
            options={ARTIFACT_KIND_FILTER_CHOICES}
            onChange={props.onKindFilterChange}
            triggerAriaLabel={t('projectDetail.artifactsToolbar.filterByType')}
            resetOptionLabel={t('projectDetail.artifactsToolbar.allTypes')}
          />
        </div>
      </PendingSkeleton>

      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectUploadAssets]}>
          <Button size="default" onClick={props.onAddArtifactsClick}>
            <PlusIcon className="size-4" />
            {t('projectDetail.artifactsToolbar.addArtifacts')}
          </Button>
        </ScopedPermission>
      </PendingSkeleton>
    </div>
  );
}
